/**
 * skyViewNode — v2 sky background TSL node that samples the baked sky-view
 * LUT instead of ray-marching per fragment (Hillaire 2020 §6 consumption).
 *
 * For each background fragment:
 *   1. Take the view direction V = `positionWorldDirection` (translation-invariant).
 *   2. Compute µ = dot(V, up) and cosLightView = dot(V, L) (sun-relative
 *      azimuth) — the same two coordinates the CPU `skyViewUV` consumes.
 *   3. Map (µ, cosLightView) → LUT UV inside the shader (mirroring the CPU
 *      `skyViewUV` math) and sample the sky-view LUT via `texture()`.
 *   4. Add an analytic sun disc (kept crisp independent of LUT resolution):
 *      a smoothstep around `dot(V, L) > cos(sunAngularRadius)`, scaled by
 *      sun illuminance.
 *
 * The non-linear zenith remap here MUST match `skyViewUV` so the GPU samples
 * the exact texels the CPU bake wrote. The azimuth remap is the squared form
 * `u = 0.5 + 0.5·sign(c)·sqrt(|c|)`.
 *
 * This node is a drop-in replacement for `SkyRenderer.buildBackgroundNode()`
 * when `pipeline: 'lut'`. It is ~1 texture fetch + a sun-disc test per
 * fragment, vs the v1 inline march's O(numSamples·sunSamples) per fragment.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, vec3, vec4, float, uniform,
  dot, normalize, sqrt, abs, sign, max, smoothstep, acos, cos, clamp, pow,
  positionWorldDirection, texture,
} from 'three/tsl';

export interface SkyViewNodeUniforms {
  /** Sun direction (FROM origin TO sun), world space. */
  sunDirection: ReturnType<typeof uniform>;
  /** Sun illuminance multiplier for the disc. */
  sunIlluminance: ReturnType<typeof uniform>;
  /** Sun disc angular radius (radians). ~0.0047 for the real sun. */
  sunAngularRadius: ReturnType<typeof uniform>;
  /** Planet bottom radius (km) — for the horizon zenith remap. */
  bottomRadius: ReturnType<typeof uniform>;
  /** Camera radius from planet centre (km). */
  viewHeightKm: ReturnType<typeof uniform>;
  /**
   * Atmospheric transmittance toward the sun (RGB), CPU-evaluated per frame
   * from the SAME atmosphere config (transmittanceToTopOfAtmosphere). This is
   * what turns the disc deep orange at the horizon and near-white at noon —
   * a flat white disc at sunset was the old look's biggest realism miss.
   */
  sunTint: ReturnType<typeof uniform>;
}

export function createSkyViewNodeUniforms(opts: {
  sunIlluminance: number;
  bottomRadius: number;
  viewHeightKm: number;
  sunAngularRadius?: number;
}): SkyViewNodeUniforms {
  return {
    sunDirection: uniform(vec3(0, 1, 0)),
    sunIlluminance: uniform(opts.sunIlluminance),
    sunAngularRadius: uniform(opts.sunAngularRadius ?? 0.0047),
    bottomRadius: uniform(opts.bottomRadius),
    viewHeightKm: uniform(opts.viewHeightKm),
    sunTint: uniform(vec3(1, 1, 1)),
  };
}

/**
 * Build the v2 background node. `skyViewTexture` is the baked sky-view LUT
 * DataTexture (Float RGBA, 192×108). The node returns a vec4 radiance.
 */
export function buildSkyViewBackgroundNode(
  skyViewTexture: THREE.DataTexture,
  u: SkyViewNodeUniforms,
): unknown {
  const evalFn = Fn(() => {
    // WHY (the "sky slides when the player moves" bug): the background sphere
    // has an IDENTITY matrixWorld (three common/Background.js), so positionWorld
    // is already a unit direction — subtracting cameraPosition swung every ray
    // with camera translation. positionWorldDirection is translation-invariant.
    const V = positionWorldDirection;
    const L = normalize(u.sunDirection);
    const up = vec3(0, 1, 0);

    const mu = dot(V, up).clamp(-1, 1);          // cosViewZenith
    const cosLightView = dot(V, L).clamp(-1, 1); // sun-relative azimuth

    // ── Zenith remap (mirror of skyViewUV, two-half sqrt around horizon) ──
    const r = max(u.viewHeightKm, u.bottomRadius.add(1e-3));
    const vHoriz = sqrt(max(float(0), r.mul(r).sub(u.bottomRadius.mul(u.bottomRadius))));
    const groundToHorizon = acos((vHoriz.div(r)).clamp(-1, 1));
    const zenithToHorizon = float(Math.PI).sub(groundToHorizon);
    const theta = acos(mu);

    // Upper half: v = 0.5·(1 − sqrt(1 − θ/Z)).  Lower half: v = 0.5 + 0.5·sqrt((θ−Z)/G).
    const upperCoord = float(1).sub(theta.div(zenithToHorizon));
    const upperV = float(0.5).mul(float(1).sub(sqrt(max(upperCoord, float(0)))));
    const lowerCoord = theta.sub(zenithToHorizon).div(groundToHorizon);
    const lowerV = float(0.5).add(float(0.5).mul(sqrt(max(lowerCoord, float(0)))));
    const isUpper = theta.lessThan(zenithToHorizon);
    const vCoord = isUpper.select(upperV, lowerV);

    // ── Azimuth remap (squared, symmetric about sun) ──
    const uCoord = float(0.5)
      .add(float(0.5).mul(sign(cosLightView)).mul(sqrt(abs(cosLightView))));

    const lutColor = texture(skyViewTexture, vec3(uCoord, vCoord, 0).xy);
    const sky = vec3(lutColor.x, lutColor.y, lutColor.z);

    // ── Analytic sun disc (limb-darkened + transmittance-tinted) ──
    // Visible where the view ray falls within the sun's angular radius:
    //   dot(V, L) > cos(sunAngularRadius). Soft edge over a few ×10⁻⁴ in
    //   cosine so the rim anti-aliases instead of stair-stepping.
    const cosV_L = dot(V, L);
    const cosRadius = cos(u.sunAngularRadius);
    const disc = smoothstep(cosRadius.sub(0.0005), cosRadius.add(0.0002), cosV_L);
    // Limb darkening (Hestroffer98 approximation): r² across the disc from
    // center (0) to rim (1); µ = cos of the emergence angle; I(µ)/I(0) ≈ µ^0.6.
    // The rim reads ~40% dimmer — the real sun's soft edge, not a flat token.
    const r2 = clamp(
      float(1).sub(cosV_L).div(max(float(1).sub(cosRadius), 1e-7)),
      0, 1,
    );
    const muLimb = sqrt(max(float(1).sub(r2), float(0)));
    const limb = pow(max(muLimb, 0.02), 0.6);
    // sunTint = CPU-evaluated transmittance toward the sun: white at noon,
    // deep orange at the horizon, fading out below it.
    const sunContribution = u.sunTint.mul(disc).mul(limb).mul(u.sunIlluminance);

    return vec4(sky.add(sunContribution), 1.0);
  });

  return evalFn();
}
